import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { InfoIcon } from '@/components/ui/Icon';
import { TrendDelta } from '@/components/ui/TrendDelta';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useCategoryScores,
  useChiefComplaints,
  useOverallStats,
} from '@/hooks/api/useDashboard';
import {
  DOMAIN_META,
  isDomainKey,
  scoreBucket,
  type DomainKey,
} from '@/data/mockPerformance';
import { lensComplaintPath, lensDomainPath } from '@/routes/paths';
import { PERIODS, usePeriod } from '@/state/PeriodContext';

import { DomainTrendModal } from './DomainTrendModal';
import { TrendsBanner } from './TrendsBanner';
import styles from './LensPage.module.css';

type SortField = 'default' | 'charts' | 'name' | 'score';
type SortDir = 'asc' | 'desc';

const SORT_FIELDS: { value: SortField; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'charts',  label: 'Charts (N)' },
  { value: 'name',    label: 'Name' },
  { value: 'score',   label: 'Score' },
];

interface ComplaintRow {
  id: string;
  name: string;
  count: number;
  score?: number | null;
  goal?: number | null;
  goalSource?: 'vida' | 'default' | null;
  trend?: 'up' | 'down' | 'neutral';
  trendDelta?: number | null;
  history?: number[];
  rank?: number | null;
  peerCount?: number | null;
}

/**
 * Lens -- Practice Analysis screen.
 *
 * Strict real-data policy: every value is either sourced from Kai-API or
 * rendered with an intentional N/A treatment. No fixtures.
 */
export function LensPage() {
  const navigate = useNavigate();
  const { domain: routeDomain } = useParams<{ domain: string }>();
  const domain: DomainKey = isDomainKey(routeDomain) ? routeDomain : 'rsq';

  const [sortField, setSortField] = useState<SortField>('default');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [legendOpen, setLegendOpen] = useState(false);
  // Sub-domain trend drill-down, relocated here from the Home RSQ tile
  // (PLA-1020). Scoped to the active domain tab.
  const [trendsOpen, setTrendsOpen] = useState(false);
  const [groupBy, setGroupBy] = useState<'complaint' | 'condition'>('complaint');
  // Condition view isn't ready yet (waiting on the gold-layer condition map),
  // so the Complaint / Condition toggle is hidden. Flip this to true to
  // re-mount the button; the Condition branch below is still wired up.
  const SHOW_GROUP_TOGGLE = false;

  const domainMeta = DOMAIN_META.find((d) => d.key === domain) ?? DOMAIN_META[0];

  // Period is shared with the Home dashboard via PeriodProvider so a selection
  // on Home carries over to Lens (and vice versa) without a re-pick.
  const { period, periodMeta, setPeriod } = usePeriod();
  const filters = { dateRange: periodMeta.api };

  const complaintsQuery = useChiefComplaints(filters);
  const scoresQuery = useCategoryScores(filters);
  const overallQuery = useOverallStats(filters);
  const onlyRsqAvailable = domain !== 'rsq';

  // Close the legend popover on Escape so keyboard users can dismiss it.
  useEffect(() => {
    if (!legendOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLegendOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [legendOpen]);

    // Map domain key -> live score (real data only; null if no API data).
  const liveScores = useMemo(() => {
    const map = new Map<DomainKey, number>();
    for (const row of scoresQuery.data ?? []) {
      if (isDomainKey(row.id) && (row.totalCharts ?? 0) > 0) {
        map.set(row.id, row.score);
      }
    }
    return map;
  }, [scoresQuery.data]);

  // Lock the domain picker until a second domain has data. While scores are
  // still loading, treat it as locked too so the control doesn't flicker.
  const domainLocked = liveScores.size < 2;

  const rows = useMemo<ComplaintRow[]>(
    () => sortRows(complaintsQuery.data ?? [], sortField, sortDir),
    [complaintsQuery.data, sortField, sortDir],
  );

  // totalCharts is consumed by the bar-list `% of charts` footer. The summary
  // context strip (Charts Analyzed / Composite RSQ) is intentionally hidden
  // for now to keep focus on the complaint list below.
  const liveCharts = overallQuery.data?.total_charts ?? 0;
  const totalCharts = liveCharts > 0 ? liveCharts : null;

  return (
    <section className={styles.page} aria-labelledby="lens-heading">
      <header className={styles.titleRow}>
        <h1 id="lens-heading" className={styles.title}>Lens</h1>
        <div className={styles.titleControls}>
          {/* Domain picker. Replaces the RSQ / FFS / VBC / IP-A pill row.
              Domains with no data are listed but disabled, and the control
              itself is locked while only one domain has data (RSQ today). */}
          <label className={styles.srOnly} htmlFor="lens-domain">Performance domain</label>
          <select
            id="lens-domain"
            className={styles.periodSelect}
            value={domain}
            disabled={domainLocked}
            title={domainLocked ? `${domainMeta.fullName} is the only domain with data` : undefined}
            onChange={(e) => {
              const next = e.target.value;
              if (isDomainKey(next)) navigate(lensDomainPath(next));
            }}
          >
            {DOMAIN_META.map((d) => (
              <option key={d.key} value={d.key} disabled={!liveScores.has(d.key)}>
                {d.label}
              </option>
            ))}
          </select>
          <label className={styles.srOnly} htmlFor="lens-period">Time period</label>
          <select
            id="lens-period"
            className={styles.periodSelect}
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value) as typeof period)}
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <div className={styles.legendAnchor}>
            <button
              type="button"
              className={styles.legendButton}
              aria-expanded={legendOpen}
              aria-controls="lens-legend"
              aria-label="Domain definitions"
              aria-haspopup="dialog"
              onClick={() => setLegendOpen((v) => !v)}
            >
              <InfoIcon width={14} height={14} />
            </button>
            {legendOpen && (
              <>
                <div
                  className={styles.legendBackdrop}
                  onClick={() => setLegendOpen(false)}
                  aria-hidden="true"
                />
                <div
                  id="lens-legend"
                  role="dialog"
                  aria-label="Performance domain definitions"
                  className={styles.legendPopover}
                >
                  <header className={styles.legendHeader}>
                    <span className={styles.legendGrip} aria-hidden="true" />
                    <h3 className={styles.legendTitle}>Performance Domains</h3>
                    <button
                      type="button"
                      className={styles.legendClose}
                      onClick={() => setLegendOpen(false)}
                      aria-label="Close"
                    >
                      x
                    </button>
                  </header>
                  <dl className={styles.legend}>
                    {DOMAIN_META.map((d) => (
                      <div key={d.key} className={styles.legendRow}>
                        <dt className={styles.legendTerm}>{d.label}</dt>
                        <dd className={styles.legendDef}>
                          <span className={styles.legendFullName}>{d.fullName}</span>
                          {d.blurb && (
                            <span className={styles.legendBlurb}>{d.blurb}</span>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Trends call-out. Collapsible so it never crowds the complaint list;
          opening the drill-down goes through the same modal as before. */}
      <TrendsBanner
        domain={domainMeta}
        filters={filters}
        onOpenTrends={() => setTrendsOpen(true)}
      />

      {/*
        Context strip (Charts Analyzed + Composite RSQ) intentionally hidden
        for now to keep the Lens tab focused on the complaint list below.
        See PLA-247 follow-up; re-mount if the strip becomes useful again.
      */}

      <article className={styles.card}>
        <header className={styles.cardHeader}>
          <span className={styles.accent} aria-hidden="true" />
          <h2 className={styles.cardTitle}>
            {groupBy === 'complaint' ? 'Complaint' : 'Condition'}
          </h2>
          <div className={styles.cardActions}>
            {SHOW_GROUP_TOGGLE && (
            <button
              type="button"
              className={styles.groupToggleBtn}
              onClick={() =>
                setGroupBy((g) => (g === 'complaint' ? 'condition' : 'complaint'))
              }
              aria-label={`Currently grouped by ${groupBy}. Tap to switch view.`}
              title={`Switch to ${groupBy === 'complaint' ? 'Condition' : 'Complaint'} view`}
            >
              <span>{groupBy === 'complaint' ? 'Condition' : 'Complaint'}</span>
              <svg
                className={styles.groupToggleSwap}
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M7 7h13" />
                <path d="M16 3l4 4-4 4" />
                <path d="M17 17H4" />
                <path d="M8 21l-4-4 4-4" />
              </svg>
            </button>
            )}
            <div className={styles.sortGroup}>
              <label className={styles.srOnly} htmlFor="lens-sort">Sort by</label>
              <select
                id="lens-sort"
                className={styles.sort}
                value={sortField}
                onChange={(e) => setSortField(e.target.value as SortField)}
              >
                {SORT_FIELDS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                type="button"
                className={styles.dirToggle}
                onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                aria-label={`Sort direction: ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
                disabled={sortField === 'default'}
                title={sortField === 'default' ? 'Pick a sort field first' : `Toggle ${sortDir === 'asc' ? 'descending' : 'ascending'}`}
              >
                {sortDir === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </header>

        {groupBy === 'condition' ? (
          <div className={styles.emptyDomain}>
            <strong>Condition view is coming soon.</strong>
            <p>
              We're building this alongside the gold-layer condition map. For now,
              switch back to <strong>By Chief Complaint</strong> to see your live data.
            </p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={() => setGroupBy('complaint')}
            >
              Back to Complaint view
            </button>
          </div>
        ) : onlyRsqAvailable ? (
          <div className={styles.emptyDomain}>
            <strong>
              {DOMAIN_META.find((d) => d.key === domain)?.fullName ?? 'Domain'} data
              isn't connected yet.
            </strong>
            <p>
              Kai-API only exposes RSQ®-scoped analytics today. Switch back to the RSQ®
              pill to see the chief-complaint list.
            </p>
          </div>
        ) : complaintsQuery.isLoading ? (
          <ul className={styles.barList} aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className={styles.barItem}>
                <div className={styles.barTop}>
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width={56} />
                </div>
                <Skeleton variant="block" height={8} />
              </li>
            ))}
          </ul>
        ) : complaintsQuery.isError ? (
          <div className={styles.errorState} role="alert">
            <strong>Couldn't load chief complaints.</strong>
            <p>{(complaintsQuery.error as Error)?.message ?? 'Unknown error contacting Kai-API.'}</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={() => complaintsQuery.refetch()}
            >
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>No chief complaints in the selected window.</p>
        ) : (
          <ul className={styles.barList}>
            {rows.map((row) => {
              const open = () => navigate(lensComplaintPath(domain, row.name));
              // Bar width reflects the per-complaint RSQ score (0-100). When the
              // score is unavailable (zero denom in the window) we keep a thin
              // visible track so the row still reads as a bar, but no fill.
              const score = row.score ?? null;
              const widthPct = score != null ? Math.max(2, score) : 0;
              const bucket = scoreBucket(score);
              const goalPct = row.goal ?? 50;
              // Goal met or surpassed -> the bar renders green regardless of
              // which score bucket the raw number falls in.
              const goalMet = score != null && score >= goalPct;
              const goalLeft = Math.min(100, Math.max(0, goalPct));
              // 'vida' = an organization-set target; otherwise the bar is
              // showing the fallback default (no goal set for this complaint).
              const goalIsSet = row.goalSource === 'vida';
              const goalTitle = goalIsSet
                ? `Organization goal ${goalPct}%`
                : `Default goal ${goalPct}% (none set)`;
              const pctOfTotal =
                totalCharts && totalCharts > 0
                  ? ((row.count / totalCharts) * 100).toFixed(1)
                  : null;
              const trend = row.trend ?? 'neutral';
              const trendDelta = row.trendDelta ?? null;
              const history = row.history ?? [];
              const rank = row.rank ?? null;
              const peerCount = row.peerCount ?? null;
              const showTrend = trend !== 'neutral' && trendDelta != null;
              const rankLabel =
                rank != null && peerCount != null ? `#${rank} of ${peerCount}` : null;
              return (
                <li
                  key={row.id}
                  className={`${styles.barItem} ${styles.barItemClickable}`}
                  onClick={open}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      open();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open detail for ${row.name}; ${row.count.toLocaleString()} charts${score != null ? `, RSQ ${score}%` : ''}${showTrend ? `, trending ${trend} ${Math.abs(trendDelta as number)} points` : ''}${rankLabel ? `, ranked ${rankLabel}` : ''}, ${goalIsSet ? 'goal' : 'default goal'} ${goalPct}%`}
                >
                  <div className={styles.barTop}>
                    <div className={styles.barLabelGroup}>
                      <span className={styles.barLabel}>{row.name}</span>
                      {showTrend && (
                        <TrendDelta
                          className={styles.barTrend}
                          delta={trendDelta}
                          direction={trend as 'up' | 'down'}
                          unit="pts"
                          size="sm"
                          title={`${trend === 'up' ? 'Up' : 'Down'} ${Math.abs(trendDelta as number)} points vs the prior period`}
                        />
                      )}
                    </div>
                    <div className={styles.barMeta}>
                      <span className={`${styles.barScore} ${styles[`score_${bucket}`]}`}>
                        {score != null ? `${score}%` : '--'}
                      </span>
                      <span className={styles.barN}>N={row.count.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className={styles.barTrack} aria-hidden="true">
                    <div
                      className={`${styles.barFill} ${goalMet ? styles.fill_surpassed : styles[`fill_${bucket}`]}`}
                      style={{ width: `${widthPct}%` }}
                    />
                    <div
                      className={styles.barGoalMarker}
                      style={{ left: `${goalLeft}%` }}
                      title={goalTitle}
                    />
                  </div>
                  <div className={styles.barFooter}>
                    <span className={styles.barFooterLeft}>
                      {rankLabel != null && (
                        <span className={styles.barRank}>{rankLabel}</span>
                      )}
                      {pctOfTotal != null && (
                        <span className={styles.barRank}>{pctOfTotal}% of charts</span>
                      )}
                      {history.length >= 2 && <Sparkline values={history} />}
                    </span>
                    <span className={styles.barGoal}>Goal {goalPct}%</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </article>

      <DomainTrendModal
        open={trendsOpen}
        onClose={() => setTrendsOpen(false)}
        domain={domainMeta}
        filters={filters}
      />
    </section>
  );
}

/**
 * Tiny inline RSQ sparkline for a complaint row. Self-scaling to its own
 * min/max so short, flat series still read as a line. Decorative only
 * (`aria-hidden`); the trend chip and aria-label carry the accessible signal.
 */
function Sparkline({ values }: { values: number[] }) {
  const w = 56;
  const h = 14;
  const pad = 1;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - pad * 2);
      const y = h - pad - ((v - min) / span) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      className={styles.barSpark}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const sortRows = (
  items: ComplaintRow[],
  field: SortField,
  dir: SortDir,
): ComplaintRow[] => {
  if (field === 'default') return items.slice();
  const arr = items.slice();
  const factor = dir === 'asc' ? 1 : -1;
  switch (field) {
    case 'charts':
      arr.sort((a, b) => factor * (a.count - b.count));
      break;
    case 'name':
      arr.sort((a, b) => factor * a.name.localeCompare(b.name));
      break;
    case 'score':
      arr.sort((a, b) => factor * ((a.score ?? 0) - (b.score ?? 0)));
      break;
    default:
      break;
  }
  return arr;
};
